import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-v5';
import {
  OccupancyAndUtilizationSummaries,
  RoundInsights,
  RoundsSummary,
  RoundVM,
} from 'spw-redux/modules/ou/types';
import SpacesCard from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/spaces-card';
import SeatsCard from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/seats-card';
import { useDispatch, useSelector } from 'react-redux';
import { TabValue } from 'spw-esa-projects/building-details/occupancy-and-utilization/types';
import { actions as ouActions } from 'spw-redux/modules/ou/actions';
import { Grid } from '@mui/material';
import UnobservedSpaces from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/unobserved-spaces';
import AverageOccupancy from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/average-occupancy';
import UnobservedSeats from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/unobserved-seats';
import AverageSeatUtilization from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/average-seat-utilization';
import AverageSeatUsedPerSpace from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/average-seat-used-per-space';
import {
  selectOccupancyAndUtilizationSummaries,
  selectProjectRounds,
} from 'spw-redux/modules/ou/selectors';
import { always, cond, equals, T } from 'ramda';
import { currentProject } from 'spw-redux/modules/projects/selectors';
import { generateRoundsSummary } from 'spw-components/occupancyAndUtilization/utils';
import { useSelectedCategories } from 'spw-esa-projects/building-details/occupancy-and-utilization/hooks';
import LowestOccupancy from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/lowest-occupancy';
import HighestOccupancy from 'spw-esa-projects/building-details/occupancy-and-utilization/cards/highest-occupancy';

const OAndUCardsExportable: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location?.search);
  const category = searchParams.get('category');
  const cardType = searchParams.get('card_type');
  const roundIds = searchParams.get('round_ids');
  const buildingId = searchParams.get('buildingId');
  const floorId = searchParams.get('floorId');
  const project = useSelector(currentProject);
  const occupancyAndUtilizationSummaries: OccupancyAndUtilizationSummaries = useSelector(
    selectOccupancyAndUtilizationSummaries
  );

  useEffect(() => {
    if (project?.id) {
      dispatch(ouActions.fetchProjectsRounds(project.id));
    }
  }, [dispatch, project?.id]);

  useEffect(() => {
    if (roundIds) {
      const ids = roundIds.split(',').map((roundId) => parseInt(roundId, 10));
      dispatch(
        ouActions.getOccupancyAndUtilizationSummaries(
          ids,
          buildingId ? parseInt(buildingId, 10) : null,
          floorId ? parseInt(floorId, 10) : null
        )
      );
    } else {
      dispatch(ouActions.cleanUpOccupancyAndUtilizationSummaries());
    }
  }, [roundIds, buildingId, floorId, dispatch]);

  const rounds = useSelector(selectProjectRounds);
  const roundsMap: { [key: number]: RoundVM } = useMemo(() => {
    return rounds.reduce((map, round) => {
      return {
        ...map,
        [round.id]: round,
      };
    }, {});
  }, [rounds]);

  const selectedRounds = useMemo(() => {
    const ids = roundIds.split(',').map((roundId) => parseInt(roundId, 10));
    if (Object.keys(roundsMap).length) {
      return ids.map((id) => {
        return roundsMap[id];
      });
    }
    return [];
  }, [roundIds, roundsMap]);

  const roundsSummary: RoundsSummary = useMemo(() => {
    if (selectedRounds) {
      return generateRoundsSummary(
        selectedRounds,
        buildingId ? parseInt(buildingId, 10) : null,
        floorId ? parseInt(floorId, 10) : null
      );
    }
    return null;
  }, [selectedRounds, buildingId, floorId]);

  const selectedCategories = useSelectedCategories(roundsSummary);

  const roundInsights: RoundInsights =
    occupancyAndUtilizationSummaries[
      category as keyof OccupancyAndUtilizationSummaries
    ] || ({} as RoundInsights);

  return (
    <div className="content">
      {cond([
        [
          equals('Spaces Occupancy Summary'),
          () => (
            <SpacesCard
              selectedCategory={category as TabValue}
              categories={selectedCategories}
              summaries={occupancyAndUtilizationSummaries}
            />
          ),
        ],
        [
          equals('Unobserved Spaces'),
          () => (
            <Grid container>
              <Grid item xs={4}>
                <UnobservedSpaces
                  category={category as TabValue}
                  summary={roundInsights.spaces}
                />
              </Grid>
            </Grid>
          ),
        ],
        [
          equals('Average Occupancy'),
          () => (
            <Grid container>
              <Grid item xs={4}>
                <AverageOccupancy
                  category={category as TabValue}
                  summary={roundInsights.spaces}
                  selectedRounds={roundsSummary.rounds}
                />
              </Grid>
            </Grid>
          ),
        ],
        [
          equals('Seats Utilization Summary'),
          () => (
            <SeatsCard
              selectedCategory={category as TabValue}
              categories={selectedCategories}
              summaries={
                occupancyAndUtilizationSummaries ||
                ({} as OccupancyAndUtilizationSummaries)
              }
            />
          ),
        ],
        [
          equals('Unobserved Seats'),
          () => (
            <Grid container>
              <Grid item xs={4}>
                <UnobservedSeats
                  category={category as TabValue}
                  summary={roundInsights.seats}
                />
              </Grid>
            </Grid>
          ),
        ],
        [
          equals('Average Seat Utilization'),
          () => (
            <Grid container>
              <Grid item xs={4}>
                <AverageSeatUtilization
                  category={category as TabValue}
                  summary={roundInsights.seats}
                  selectedRounds={roundsSummary.rounds}
                />
              </Grid>
            </Grid>
          ),
        ],
        [
          equals('Average Seats Used Per Space'),
          () => (
            <Grid container>
              <Grid item xs={4}>
                <AverageSeatUsedPerSpace
                  category={category as TabValue}
                  spacesSummary={roundInsights.spaces}
                  seatsSummary={roundInsights.seats}
                  selectedRounds={roundsSummary.rounds}
                />
              </Grid>
            </Grid>
          ),
        ],
        [
          equals('Lowest Occupancy'),
          () => {
            const roundId = searchParams.get('round_id');
            const roundWithLowestOccupancy = roundInsights.roundsWithLowestOccupancy?.find(
              (round) => round.id === parseInt(roundId, 10)
            );

            if (roundWithLowestOccupancy) {
              return (
                <Grid container>
                  <Grid item xs={4}>
                    <LowestOccupancy
                      category={category as TabValue}
                      round={roundWithLowestOccupancy}
                    />
                  </Grid>
                </Grid>
              );
            }
            return null;
          },
        ],
        [
          equals('Highest Occupancy'),
          () => {
            const roundId = searchParams.get('round_id');
            const roundWithHighestOccupancy = roundInsights.roundsWithHighestOccupancy?.find(
              (round) => round.id === parseInt(roundId, 10)
            );
            if (roundWithHighestOccupancy) {
              return (
                <Grid container>
                  <Grid item xs={4}>
                    <HighestOccupancy
                      category={category as TabValue}
                      round={roundWithHighestOccupancy}
                    />
                  </Grid>
                </Grid>
              );
            }
            return null;
          },
        ],
        [T, always(null)],
      ])(cardType)}
    </div>
  );
};

export default OAndUCardsExportable;
